import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from 'react';

import { cn } from '../../lib/utils';
import { usePrivatePortalTarget } from './private-portal';

export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;

function PopoverPortal({
  children,
  ...props
}: ComponentPropsWithoutRef<typeof PopoverPrimitive.Portal>) {
  const privateTarget = usePrivatePortalTarget();
  if (privateTarget === null) return null;
  return (
    <PopoverPrimitive.Portal {...props} container={privateTarget ?? props.container}>
      {children}
    </PopoverPrimitive.Portal>
  );
}

export const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <PopoverPortal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      // Keep the popover off the viewport edge (its shadow/border no longer
      // "touch" the screen = the "cut off" impression) and never taller than the
      // available space — scroll internally instead of clipping (Delete actions
      // etc. stay reachable on landscape phones).
      collisionPadding={collisionPadding}
      className={cn(
        'z-50 max-h-[var(--radix-popover-content-available-height)] w-72 overflow-y-auto rounded-md border border-border bg-popover p-4 text-popover-foreground shadow-md outline-none',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  </PopoverPortal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
